import React, { useEffect, useRef, useState } from 'react';

import ContactIcon from 'bundles/backoffice/contacts/icon';
import QuickbooksStatusIcon from 'bundles/backoffice/quickbooks/status_icon';
import useOnScreen from 'hooks/use_on_screen';
import { updateContactQuickbooksId } from 'bundles/backoffice/contacts/api';

import type { Contact, ContactRole } from 'bundles/backoffice/contacts/types';
import type { QuickbooksEntity } from 'bundles/backoffice/quickbooks/types';

export interface Props {
  contactRole: ContactRole;
  features?: Record<string, boolean>;
  hasSiblingAbove?: boolean;
  hasSiblingBelow?: boolean;
  quickbooksStatus?: 'customer' | 'vendor';
  onSaveContact?: (contact: Contact) => void;
}

export function ContactsCardContactIcon({
  contactRole,
  features,
  hasSiblingAbove,
  hasSiblingBelow,
  quickbooksStatus,
  onSaveContact,
}: Props) {
  const avatarRef = useRef<HTMLSpanElement>(null);
  const isOnScreen = useOnScreen(avatarRef);
  const [avatarCenter, setAvatarCenter] = useState<{ x: number, y: number } | null>(null);

  useEffect(() => {
    if (avatarRef.current && isOnScreen) {
      setAvatarCenter({
        x: avatarRef.current.offsetLeft + avatarRef.current.offsetWidth / 2,
        y: avatarRef.current.offsetTop + avatarRef.current.offsetHeight / 2,
      });
    }
  }, [isOnScreen]);

  let connectorStyle: React.CSSProperties | undefined;
  if (avatarCenter) {
    if (hasSiblingAbove && hasSiblingBelow) {
      connectorStyle = { top: 0, bottom: 0 };
    } else if (hasSiblingAbove) {
      connectorStyle = { top: 0, height: avatarCenter.y };
    } else if (hasSiblingBelow) {
      connectorStyle = { top: avatarCenter.y, bottom: 0 };
    }
  }

  const showQuickbooksStatus = quickbooksStatus && features?.quickbooksAccountingIntegration;
  const quickbooksId = quickbooksStatus === 'customer'
    ? contactRole.contact.quickbooksCustomerId
    : contactRole.contact.quickbooksVendorId;

  return <>
    {avatarCenter && connectorStyle &&
      <div
        className="contact-card__connector position-absolute z-1"
        style={{ ...connectorStyle, left: avatarCenter.x, width: 1 }}
      />
    }

    {/* The avatar clips its own overflow, so the badge is a sibling rendered over this wrapper.
        align-self-start keeps the wrapper sized to the avatar instead of stretching to the row. */}
    <span className="d-inline-flex align-self-start position-relative z-2" ref={avatarRef}>
      <ContactIcon contact={contactRole.contact} />

      {showQuickbooksStatus &&
        <QuickbooksStatusIcon
          buildoutObject={contactRole.contact as unknown as Record<string, unknown>}
          className="contact-card__qb-badge position-absolute"
          connected={!!quickbooksId}
          quickbooksId={quickbooksId}
          type={quickbooksStatus}
          onChange={async (entity: QuickbooksEntity | null) => {
            const type = quickbooksStatus === 'customer' ? 'customer' : 'vendor';
            const newQuickbooksId = entity ? Number(entity.id) : null;
            await updateContactQuickbooksId(contactRole.contact.id, type, newQuickbooksId);
            onSaveContact?.({
              ...contactRole.contact,
              [type === 'customer' ? 'quickbooksCustomerId' : 'quickbooksVendorId']: newQuickbooksId,
            });
          }}
        />
      }
    </span>
  </>;
}
